import React, { useEffect, useRef, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import TextInput from "../../common/textfield/TextInput";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import Select from "react-select";
import { FormikObj, categoryOptions, filterStatusOptions, handleExt } from "./Constant";
import SelectFiled from "../../common/textfield/SelectFiled";
import { Tooltip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useSelector } from "react-redux";
import InputFile from "../../common/textfield/InputFile";
import { handleFileUpload } from "../../../utils/ImageToBase64";
import { getPatientList, getPractitionerAuthorizedList } from "../../api/Individual";
import { decryptData } from "../../EncryptDecrypt";
import { getLocationQuery, getOrgnizationForLocation } from "../../api/ClinicLocation";
import { addDocumentDashboard } from "../../api/FillableDocument.js";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";

function UploadDocument() {
  const navigate = useNavigate();
  const userRoles = useSelector((state) => state?.auth?.user?.["cognito:groups"]);
  const user = useSelector((state) => state?.auth?.user);

  const [btnLoading, setBtnLoading] = useState(false);
  const [practionerList, setPractionerList] = useState([]);
  const [patientList, setPatientList] = useState([]);
  const [isPatientFetched, setIsPatientFetched] = useState(false);
  const [isPractitionerFetched, setIsPractitionerFetched] = useState(false);

  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(false);

  const [file, setFile] = useState();
  const userData = useRef();

  const [organizations, setOrganizations] = useState([]);
  const [orgId, setOrgId] = useState(user["custom:orgId"]);

  useOrgQuery({
    onSuccess: (data) => {
      setOrganizations(data?.map((org) => ({ label: org?.name, value: org?.id })));
    },
    refetchOnWindowFocus: false,
  });

  const handleOrgChange = (option) => {
    const newOrgId = option?.value || null;
    setOrgId(newOrgId);

    // Reset the form to initial values, but keep organization + default status
    formik.resetForm({
      values: {
        ...FormikObj.initialValues,
        organization: newOrgId,
        status: "new",
        locationId: "",
        locationName: "",
        practitionerId: "",
        selectedPractitionerName: "",
        patientId: "",
        assignPatient: "",
        document: "",
      },
    });

    setFile(undefined);
  };

  const formik = useFormik({
    ...FormikObj,
    onSubmit: async (values) => {
      setBtnLoading(true);

      if (values?.document && values?.document.type !== "application/pdf") {
        formik.setFieldError("document", "Please upload a PDF file.");
        setBtnLoading(false);
        return;
      }

      let base64Document;
      if (values?.document) {
        base64Document = await handleFileUpload(values?.document);
        base64Document = base64Document.split(",")[1];
      }
      const document = { data: base64Document, extension: "pdf" };

      addDocumentDashboard({
        ...values,
        isForSRFax: true,
        code: "manual",
        orgId,
        document,
        loginPractitionerName: user?.name?.[0]?.text,
      })
        .then((res) => {
          if (res.status === true) {
            navigate("/app/document-dashboard");
            success(res.message);
          } else {
            const parsedMessage = JSON.parse(res.message);
            failed(parsedMessage.message);
          }
        })
        .catch((res) => {
          failed(res?.response?.data?.message || res?.response?.data?.error || res.message);
        })
        .finally(() => setBtnLoading(false));
    },
  });

  // ---- Handlers
  const handleSource = (e) => {
    const val = e?.value ?? null;
    formik.setFieldValue("locationId", val);
    formik.setFieldValue(
      "locationName",
      val === "other" ? "Other" : (locations.find((obj) => obj?.value === val)?.label || "")
    );
  };

  const handleCategory = (e) => {
    formik.setFieldValue("categoryCode", e.target.value);
    formik.setFieldValue(
      "categoryDisplay",
      categoryOptions?.find((obj) => obj?.value === e.target.value)?.name
    );
  };

  const handlePractitionerChange = (e) => {
    const val = e?.value ?? null;
    formik.setFieldValue("practitionerId", val);
    formik.setFieldValue(
      "selectedPractitionerName",
      val ? practionerList?.find((obj) => obj?.value === val)?.label || "" : ""
    );
    formik.setFieldValue("status", val ? "assigned" : "new");
  };

  const handlePatientChange = (e) => {
    const val = e?.value ?? null;
    formik.setFieldValue("patientId", val);
    formik.setFieldValue(
      "assignPatient",
      val ? patientList?.find((obj) => obj?.value === val)?.label || "" : ""
    );
  };

  const handlePagesChange = (e) => {
    let inputValue = e.target.value;
    if (inputValue.startsWith("0") && inputValue !== "0") {
      const newValue = inputValue.replace(/^0+/, "");
      formik.setFieldValue("pages", newValue);
    } else if (inputValue === "0") {
      formik.setFieldValue("pages", "");
    } else {
      formik.setFieldValue("pages", inputValue);
    }
  };

  // ---- Fetchers scoped to selected org
  const fetchLocationsForOrg = async (orgIdArg) => {
    setLocLoading(true);
    try {
      await getOrgnizationForLocation();
      const data = await getLocationQuery({ orgnizationIds: [orgIdArg], userRoles });
      const opts = (data?.data || [])
        .map((obj) => ({
          id: obj?.id,
          value: obj?.id,
          label: obj?.name
            ?.split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" "),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setLocations([...opts, { id: "other", value: "other", label: "Other" }]);
    } catch (e) {
      setLocations([]);
      console.error("error fetching locations", e);
    } finally {
      setLocLoading(false);
    }
  };

  const fetchPractitionersForOrg = async (orgIdArg) => {
    setIsPractitionerFetched(false);
    try {
      const res = await getPractitionerAuthorizedList(orgIdArg);
      const practs = res?.result || [];
      const unsorted = practs.filter((item) => item.name);
      setPractionerList(
        unsorted
          .map((ele) => ({
            id: ele.id,
            label: decryptData(
              ele.name[0]?.text
                ?.split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")
            ),
            value: ele.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (e) {
      setPractionerList([]);
      console.log("error fetching practitioner list", e);
    } finally {
      setIsPractitionerFetched(true);
    }
  };

  const fetchPatientsForOrg = async (orgIdArg) => {
    setIsPatientFetched(false);
    try {
      const res = await getPatientList(orgIdArg);
      setPatientList(
        (res?.result || [])
          .map((ele) => ({
            id: ele.id,
            label: decryptData(
              ele.name[0]?.text
                ?.split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")
            ),
            value: ele.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (e) {
      setPatientList([]);
      console.log("error fetching patient list", e);
    } finally {
      setIsPatientFetched(true);
    }
  };

  // ---- When org changes: fetch org-scoped data + clear dependent fields
  useEffect(() => {
    if (!orgId) {
      setLocations([]);
      setPractionerList([]);
      setPatientList([]);
      return;
    }

    // Clear dependent selections with null so react-select clears visually
    formik.setFieldValue("locationId", "");
    formik.setFieldValue("locationName", "");
    formik.setFieldValue("practitionerId", "");
    formik.setFieldValue("selectedPractitionerName", "");
    formik.setFieldValue("patientId", "");
    formik.setFieldValue("assignPatient", "");
    formik.setFieldValue("document", "");

    fetchLocationsForOrg(orgId);
    fetchPractitionersForOrg(orgId);
    fetchPatientsForOrg(orgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  return (
    <section className="common-listing">
      <div className="heading-wrap mb-3">
        <Tooltip title="Go back">
          <div style={{ marginRight: "1rem", cursor: "pointer" }} onClick={() => navigate(-1)}>
            <ArrowBack />
          </div>
        </Tooltip>
      </div>
      <div className="custom-card p-4">
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <Row>
            {organizations.length >= 1 && (
              <Col>
                <label className="searchSelectLabel">
                  Organization<span style={{ color: "red" }}> *</span>{" "}
                </label>
                <Select
                  keyField={"organization"}
                  label={"Select Organization"}
                  selectLabel="Select Organization"
                  options={organizations}
                  isLoading={organizations?.length >= 1 ? false : true}
                  readOnly={organizations?.length >= 1 ? false : true}
                  hideRequired={true}
                  onChange={handleOrgChange}
                  className="search-select"
                  classNamePrefix="select"
                  placeholder="Select Organization..."
                  // controlled
                  value={organizations.find((o) => o.value === orgId) || null}
                />
              </Col>
            )}

            <Col>
              <div className="field-wrap">
                <label className="searchSelectLabel">
                  Source <span style={{ color: "red" }}> *</span>{" "}
                </label>
                <Select
                  onChange={handleSource}
                  isMulti={false}
                  name="locationId"
                  options={locations}
                  className="search-select"
                  classNamePrefix="select"
                  isDisabled={!orgId || locLoading}
                  isLoading={!!orgId && locLoading}
                  placeholder={orgId ? "Select location" : "Pick an organization first"}
                  noOptionsMessage={() =>
                    orgId ? "No locations found" : "Pick an organization first"
                  }
                  // controlled
                  value={
                    formik.values.locationId
                      ? locations.find((o) => o.value === formik.values.locationId) || null
                      : null
                  }
                />
                <div style={{ marginTop: "15px" }}>
                  {formik?.touched["locationId"] && formik?.errors["locationId"] ? (
                    <div className="error-text">{formik?.errors["locationId"]}</div>
                  ) : null}
                </div>
              </div>
            </Col>

            <Col>
              <SelectFiled
                keyField={"categoryCode"}
                label={"Category"}
                formik={formik}
                options={categoryOptions}
                onChange={handleCategory}
              />
            </Col>

            <Col>
              <TextInput
                keyField={"pages"}
                label={"Pages"}
                type="text"
                formik={formik}
                placeholder={"Pages"}
                onChange={handlePagesChange}
                onKeyPress={handleExt}
                autoComplete="off"
                required={false}
              />
            </Col>

            <Col>
              <SelectFiled
                keyField={"status"}
                label={"Status"}
                formik={formik}
                options={filterStatusOptions}
                disabled={true}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="field-wrap">
                <label className="searchSelectLabel">Assign Practitioner (Optional)</label>
                <Select
                  onChange={handlePractitionerChange}
                  isMulti={false}
                  name="practitionerId"
                  options={practionerList}
                  className="search-select"
                  classNamePrefix="select"
                  isClearable={true}
                  isDisabled={!orgId || !isPractitionerFetched}
                  isLoading={!!orgId && !isPractitionerFetched}
                  placeholder={orgId ? "Select practitioner" : "Pick an organization first"}
                  // controlled
                  value={
                    formik.values.practitionerId
                      ? practionerList.find((o) => o.value === formik.values.practitionerId) ||
                        ""
                      : ""
                  }
                />
                <div>
                  {formik?.touched["practitionerId"] && formik?.errors["practitionerId"] ? (
                    <div className="error-text">{formik?.errors["practitionerId"]}</div>
                  ) : null}
                </div>
              </div>
            </Col>

            <Col>
              <div className="field-wrap">
                <label className="searchSelectLabel">Assign Patient (Optional)</label>
                <Select
                  onChange={handlePatientChange}
                  isMulti={false}
                  name="patientId"
                  options={patientList}
                  className="search-select"
                  classNamePrefix="select"
                  isClearable={true}
                  isDisabled={!orgId || !isPatientFetched}
                  isLoading={!!orgId && !isPatientFetched}
                  placeholder={orgId ? "Select patient" : "Pick an organization first"}
                  value={
                    formik.values.patientId
                      ? patientList.find((o) => o.value === formik.values.patientId) || null
                      : null
                  }
                />
                <div>
                  {formik?.touched["patientId"] && formik?.errors["patientId"] ? (
                    <div className="error-text">{formik?.errors["patientId"]}</div>
                  ) : null}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mt-5">
            <Col>
              <InputFile
                keyField={"document"}
                label={"Upload Document"}
                formik={formik}
                placeholder={"Upload Document"}
                required={true}
                setFile={setFile}
                file={file}
                accept="application/pdf"
                imageUrl={userData?.current?.photo?.url}
              />
            </Col>
          </Row>

          <div className="btn-wrap" style={{ display: "flex" }}>
            <Button
              onClick={() => navigate("/app/document-dashboard")}
              variant="secondary"
              title="Cancel"
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default UploadDocument;